import type { ProfileReviewStatus } from "@shared/types";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";

const NOT_PROVIDED = "Not provided";

/**
 * The contractor profile fields the Users page detail cast needs. Kept
 * local rather than added to `packages/shared-types` -- the backend already
 * returns the full `contractorProfiles` row on the user detail endpoint, and
 * this mirrors the same local-cast approach `realtorProfileOf` uses in
 * users-dashboard.tsx for the same reason: the shared `AdminUserRecord.profile`
 * type stays intentionally narrow, so callers that need more cast to a
 * shape matching what the endpoint is known to send.
 */
export interface ReviewableContractorProfile {
	id: string;
	trades?: string[] | null;
	experienceYears?: number | null;
	serviceAreas?: string[] | null;
	workPhotos?: string[] | null;
	bio?: string | null;
	isVerified: boolean;
	reviewStatus: ProfileReviewStatus;
	reviewNotes?: string | null;
}

function DetailField({
	label,
	children,
}: {
	label: string;
	children: ReactNode;
}) {
	return (
		<div className="grid gap-1">
			<span className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
				{label}
			</span>
			{children}
		</div>
	);
}

function textOrNotProvided(value: string | null | undefined): string {
	return value?.trim() || NOT_PROVIDED;
}

function listOrNotProvided(values: string[] | null | undefined): string {
	return values?.length ? values.join(", ") : NOT_PROVIDED;
}

function yearsOrNotProvided(years: number | null | undefined): string {
	if (years === null || years === undefined) return NOT_PROVIDED;
	return years === 1 ? "1 year" : `${years} years`;
}

/** A count, never the URLs themselves -- these files are only meaningful to open, not to list. */
function imageCount(values: string[] | null | undefined, noun: string): string {
	const count = values?.length ?? 0;
	return `${count} ${count === 1 ? noun : `${noun}s`}`;
}

function ReviewStatusBadge({ status }: { status: ProfileReviewStatus }) {
	const variant =
		status === "approved"
			? "success"
			: status === "rejected"
				? "danger"
				: "warning";
	return <Badge variant={variant}>{status}</Badge>;
}

/**
 * A contractor's trades, service areas and work photo summary, shown in the
 * Users page detail sheet the same way a realtor's profile is -- the backend
 * already returns the full `contractorProfiles` row on the user detail
 * endpoint.
 */
export function ContractorReviewDetails({
	profile,
}: {
	profile: ReviewableContractorProfile;
}) {
	return (
		<Card className="border-slate-200 shadow-none">
			<CardHeader>
				<CardTitle>Contractor details</CardTitle>
				<CardDescription>
					The trades, service areas and work photo summary from the contractor's
					profile.
				</CardDescription>
			</CardHeader>
			<CardContent className="grid gap-4 text-sm">
				<DetailField label="Trades">
					<span className="font-medium">
						{listOrNotProvided(profile.trades)}
					</span>
				</DetailField>
				<DetailField label="Experience">
					<span>{yearsOrNotProvided(profile.experienceYears)}</span>
				</DetailField>
				<DetailField label="Service areas">
					<span>{listOrNotProvided(profile.serviceAreas)}</span>
				</DetailField>
				<DetailField label="Work photos">
					<span>{imageCount(profile.workPhotos, "photo")}</span>
				</DetailField>
				<DetailField label="Bio">
					<span>{textOrNotProvided(profile.bio)}</span>
				</DetailField>
				<DetailField label="Verification">
					<div className="flex flex-wrap gap-2">
						<Badge variant={profile.isVerified ? "success" : "warning"}>
							{profile.isVerified ? "Verified" : "Not verified"}
						</Badge>
						<ReviewStatusBadge status={profile.reviewStatus} />
					</div>
				</DetailField>
				<DetailField label="Review notes">
					<span>{textOrNotProvided(profile.reviewNotes)}</span>
				</DetailField>
			</CardContent>
		</Card>
	);
}
